import { useCallback, useEffect, useRef, useState } from 'react';
import * as reactiveUtils from '@arcgis/core/core/reactiveUtils';
import { Box } from '@cad-arc';
import {
    ESRI_MAP_DEFAULT_ZOOM_LEVEL,
    MAP_ALTERNATIVE_BASEMAP_ACTIVATION_DATE_KEY,
} from '../configurations';
import { MapView, Point, WebMap, Color } from '../constructors';
import { storageHelpers } from '../../common/helpers/browser-storage';
import { isValidLatLng } from '../../location/helpers';
import type { GeoPoint } from '../../location/types';
import type { EsriMapInterface } from '../types';
import { logMapError, logMapWarn, setupWebGLContextListeners } from '../helpers/utils';
import { incrementFatalErrorCount, markMapCreated } from '../helpers/map-diagnostic-context';
import { createMapRecoveryTracker } from '../helpers/map-recovery-tracker';
import type { MapRecoveryTracker } from '../helpers/map-recovery-tracker';
import { useCollectMapDiagnosticContext } from '../helpers/collect-map-diagnostic-context';
import { useEnabledFeatures } from '../../common/hooks/enabled-features/use-enabled-features';
import { createInitialWebMap, shouldLoadAlternativeBasemap } from './helpers';
import { useEsriMapStore } from '~/map-window/hooks/use-esri-map-store';
import { colors } from '~/colors/colors';

type UseEsriEmbeddedESModuleMap = {
    center: GeoPoint;
    mapRef: React.MutableRefObject<HTMLDivElement | null>;
};
const useEsriEmbeddedESModuleMap = ({
    center,
    mapRef,
}: UseEsriEmbeddedESModuleMap): EsriMapInterface => {
    const { mapMode: storeMapMode } = useEsriMapStore();
    const enabledFeatures = useEnabledFeatures();
    const isDiagnosticContextEnabled = enabledFeatures.diagnosticContextEnabled === true;
    const isDiagnosticContextEnabledRef = useRef(isDiagnosticContextEnabled);
    isDiagnosticContextEnabledRef.current = isDiagnosticContextEnabled;
    const enabledFeaturesRef = useRef(enabledFeatures);
    enabledFeaturesRef.current = enabledFeatures;

    const collectDiagnosticContext = useCollectMapDiagnosticContext();
    const collectDiagnosticContextRef = useRef(collectDiagnosticContext);
    collectDiagnosticContextRef.current = collectDiagnosticContext;

    const [webMap, setWebMap] = useState<WebMap>();
    const [mapView, setMapView] = useState<MapView>();
    const [mapMode, setMapMode] = useState<string>('');
    const [isWebGL2Supported, setIsWebGL2Supported] = useState<boolean>(true);
    const [isMapLoading, setIsMapLoading] = useState<boolean>(true);
    const [isMapLoadingFailed, setIsMapLoadingFailed] = useState<boolean>(false);
    const cleanupWebGLListenersRef = useRef<(() => void) | undefined>();
    const recoveryTrackerRef = useRef<MapRecoveryTracker | undefined>();

    const setMapLoadFailed = useCallback((failed: boolean) => {
        if (failed) {
            storageHelpers.setItem(
                MAP_ALTERNATIVE_BASEMAP_ACTIVATION_DATE_KEY,
                new Date().toISOString()
            );
        }
        setIsMapLoadingFailed(failed);
    }, []);

    useEffect(
        function initializeMap() {
            if (
                mapMode === storeMapMode &&
                (!!mapView || !isValidLatLng(center.latitude, center.longitude))
            ) {
                return;
            }

            const isAlternativeBasemap = shouldLoadAlternativeBasemap();

            const webmap = createInitialWebMap({
                isAlternativeBasemap,
                isEmbeddedBasemap: true,
                mapMode: storeMapMode,
            });

            const view = new MapView({
                container: mapRef.current ?? undefined,
                map: webmap,
                center: center as Point,
                zoom: ESRI_MAP_DEFAULT_ZOOM_LEVEL,
                constraints: {
                    rotationEnabled: false,
                },
                ui: undefined,
                highlightOptions: {
                    color: new Color(colors.esriColors.highlightColor),
                    fillOpacity: 0.16,
                    haloOpacity: 1,
                },
            });

            markMapCreated();

            view.when(() => {
                // Collapses raw WebGL failure/recovery signals into per-episode
                // `recovery_succeeded` / `recovery_failed` outcome events.
                // Additive — reads the existing signals below, suppresses nothing.
                recoveryTrackerRef.current = createMapRecoveryTracker({
                    isEmbeddedMap: true,
                    enabledFeatures: enabledFeaturesRef.current as unknown as Record<string, boolean>,
                    getFatalErrorName: () => view.fatalError?.name,
                });

                // Diagnostic context collection is async (IPC to electron main).
                // We use `void (async () => { ... })()` instead of `.then()` because
                // Esri's reactiveUtils.when (and view.when) consume the return value
                // of their callbacks — if the callback returns a promise, Esri
                // intercepts it and the chained `.then()` never fires.
                cleanupWebGLListenersRef.current = setupWebGLContextListeners(
                    mapRef,
                    true,
                    (event, canvas) => {
                        if (isDiagnosticContextEnabledRef.current) {
                            void (async () => {
                                try {
                                    const ctx = await collectDiagnosticContextRef.current(view, mapRef, true);
                                    logMapError({
                                        source: 'webgl',
                                        event: 'context_lost',
                                        isEmbeddedMap: true,
                                        extraData: {
                                            statusMessage: event.statusMessage,
                                            canvasWidth: canvas.width,
                                            canvasHeight: canvas.height,
                                            diagnosticContext: ctx,
                                        },
                                    });
                                } catch {
                                    logMapError({
                                        source: 'webgl',
                                        event: 'context_lost',
                                        isEmbeddedMap: true,
                                        extraData: {
                                            statusMessage: event.statusMessage,
                                            canvasWidth: canvas.width,
                                            canvasHeight: canvas.height,
                                        },
                                    });
                                }
                            })();
                        } else {
                            logMapError({
                                source: 'webgl',
                                event: 'context_lost',
                                isEmbeddedMap: true,
                                extraData: {
                                    statusMessage: event.statusMessage,
                                    canvasWidth: canvas.width,
                                    canvasHeight: canvas.height,
                                },
                            });
                        }
                    },
                    () => recoveryTrackerRef.current?.onRecoverySignal('context_restored')
                );

                // Guard so we only log + attempt recovery once per map instance.
                // Without this, the SDK re-emits fatalError after a failed
                // tryFatalErrorRecovery(), creating an unbounded error loop.
                let hasAttemptedRecovery = false;

                reactiveUtils.when(
                    () => view.fatalError,
                    (fatalError) => {
                        if (hasAttemptedRecovery) {
                            return;
                        }
                        hasAttemptedRecovery = true;

                        incrementFatalErrorCount();
                        if (isDiagnosticContextEnabledRef.current) {
                            void (async () => {
                                try {
                                    const ctx = await collectDiagnosticContextRef.current(view, mapRef, true);
                                    logMapError({
                                        source: 'sdk',
                                        event: 'fatal_error',
                                        isEmbeddedMap: true,
                                        extraData: {
                                            errorName: fatalError?.name,
                                            errorMessage: fatalError?.message,
                                            diagnosticContext: ctx,
                                        },
                                    });
                                } catch {
                                    logMapError({
                                        source: 'sdk',
                                        event: 'fatal_error',
                                        isEmbeddedMap: true,
                                        extraData: {
                                            errorName: fatalError?.name,
                                            errorMessage: fatalError?.message,
                                        },
                                    });
                                }
                            })();
                        } else {
                            logMapError({
                                source: 'sdk',
                                event: 'fatal_error',
                                isEmbeddedMap: true,
                                extraData: {
                                    errorName: fatalError?.name,
                                    errorMessage: fatalError?.message,
                                },
                            });
                        }

                        if (fatalError?.name === 'webgl-context-lost') {
                            recoveryTrackerRef.current?.onFailure();
                        }
                        view.tryFatalErrorRecovery();
                    }
                );

                // Watch for fatalError CLEARING (truthy -> falsy) as a recovery
                // signal for the SDK fatal-error path — covers recoveries that
                // the browser `webglcontextrestored` event may not surface.
                reactiveUtils.watch(
                    () => view.fatalError,
                    (fatalError, previousFatalError) => {
                        if (previousFatalError && !fatalError) {
                            recoveryTrackerRef.current?.onRecoverySignal('fatal_error_cleared');
                        }
                    }
                );

                // readyState watcher — log non-fatal SDK state transitions for
                // diagnostics. The readyState value is included so we can
                // investigate specific failure modes.
                reactiveUtils.watch(
                    // readyState exists at runtime but is missing from @arcgis/core@4.31 type definitions
                    () => (view as unknown as { readyState: string }).readyState,
                    (readyState: string) => {
                        if (readyState !== 'ready') {
                            logMapWarn({
                                source: 'sdk',
                                event: 'ready_state_change',
                                isEmbeddedMap: true,
                                extraData: { readyState },
                            });
                        }
                    }
                );
            }).catch((error) => {
                setMapLoadFailed(true);
                if (error.name?.includes('webgl')) {
                    setIsWebGL2Supported(false);
                    logMapError({
                        source: 'webgl',
                        event: 'not_supported',
                        isEmbeddedMap: true,
                        error,
                    });
                } else {
                    logMapError({
                        source: 'sdk',
                        event: 'view_init_error',
                        isEmbeddedMap: true,
                        error,
                    });
                }
            });

            view.watch('ready', () => {
                setIsMapLoading(false);
            });

            setWebMap(webmap);
            setMapView(view);
            setMapMode(storeMapMode);
        },
        [mapView, setMapLoadFailed, center, mapRef, storeMapMode, mapMode]
    );

    // separate effect to destroy the esri map instance
    // because the effect above will re-trigger due to a change in
    // `mapView` and instantly destroy the map again
    useEffect(() => {
        if (!mapView) {
            return;
        }

        return () => {
            recoveryTrackerRef.current?.dispose();
            cleanupWebGLListenersRef.current?.();
            if (!mapView.destroyed) {
                mapView.destroy();
            }
        };
    }, [mapView]);

    return { mapView, webMap, isWebGL2Supported, isMapLoading, isMapLoadingFailed };
};

type UseEsriEmbeddedBaseMapProps = { center: GeoPoint };

export const useEsriEmbeddedBaseMap = ({
    center,
}: UseEsriEmbeddedBaseMapProps): EsriMapInterface & {
    mapComponent: React.ReactElement;
    mapRef: React.MutableRefObject<HTMLDivElement | null>;
} => {
    const mapRef = useRef<HTMLDivElement | null>(null);
    const mapComponent = <Box w="100%" h="100%" ref={mapRef} />;
    const { mapView, webMap, isWebGL2Supported, isMapLoading, isMapLoadingFailed } =
        useEsriEmbeddedESModuleMap({ center, mapRef });

    return {
        mapComponent,
        mapView,
        webMap,
        mapRef,
        isWebGL2Supported,
        isMapLoading,
        isMapLoadingFailed,
    };
};
